import { REGULAR_PRICE_TYPE } from './constants';

/**
 * Reading the prices of a price_mismatch task.
 *
 * A shelf label can carry several prices, so the API exposes a `prices` list
 * with one entry per price. Stores migrate to that shape one at a time, and a
 * store still served from Cloud SQL returns only the flat `price`/`pos_price`
 * pair — so every consumer here normalizes both into the same list and gets a
 * single code path.
 *
 * Shared by web and mobile: the "how many prices" and "is this a plain regular
 * price" questions drive what the list card shows, and both apps must answer
 * them the same way.
 */

/**
 * Normalizes a task's prices into a single list, whatever shape the API sent.
 *
 * @param {object} task - Task information object.
 * @returns {{ price_type: string|null, price: any, pos_price: any, alert_id: string|null }[]}
 *   One entry per price, in the order the API sent them (regular first). Empty
 *   when the task carries no price at all.
 */
export function resolveTaskPrices(task) {
  if (Array.isArray(task?.prices) && task.prices.length > 0) {
    return task.prices.map(price => ({
      price_type: price?.price_type ?? null,
      price: price?.price ?? null,
      pos_price: price?.pos_price ?? null,
      alert_id: price?.alert_id ?? null,
    }));
  }

  // Cloud SQL shape: a single unlabelled pair. price_type stays null so callers
  // can tell "the API did not say" apart from "this is the regular price".
  if (task?.price != null || task?.pos_price != null) {
    return [
      {
        price_type: null,
        price: task?.price ?? null,
        pos_price: task?.pos_price ?? null,
        alert_id: null,
      },
    ];
  }

  return [];
}

/**
 * Whether the task's prices should be summarized instead of shown in full.
 *
 * The list card has no room for several price pairs, so a multi-price task shows
 * a count and moves the detail behind a tap.
 *
 * @param {object} task - Task information object.
 * @returns {boolean} True when the task carries more than one price.
 */
export function hasMultiplePrices(task) {
  return resolveTaskPrices(task).length > 1;
}

/**
 * Whether a single price needs its price_type spelled out.
 *
 * A lone `regular` price is what the card has always shown, so labelling it adds
 * noise. Any other type does need the label: a promotional-only task is the
 * common case in stores that run promotions, and showing "Price: $24.95" with no
 * hint that it is the promotional one sends the associate to the wrong number on
 * the shelf label.
 *
 * @param {object} task - Task information object.
 * @returns {boolean} True when there is exactly one price and it is neither
 *   regular nor unlabelled.
 */
export function needsPriceTypeLabel(task) {
  const prices = resolveTaskPrices(task);
  if (prices.length !== 1) {
    return false;
  }
  const priceType = prices[0].price_type;
  return priceType != null && priceType !== REGULAR_PRICE_TYPE;
}
